import { Stack, Theme, Typography } from "@mui/material";
import {
  dayCalendarClasses,
  LocalizationProvider,
  pickerDayClasses,
  pickersCalendarHeaderClasses,
  pickersInputBaseClasses,
} from "@mui/x-date-pickers";
import {
  DateRange,
  dateRangePickerDayClasses,
  DateRangeValidationError,
  MultiInputDateRangeField,
  StaticDateRangePicker,
} from "@mui/x-date-pickers-pro";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { PickerRangeValue } from "@mui/x-date-pickers/internals";
import { isValid } from "date-fns";
import { getCurrentUtcDate } from "../../lib/Util";
import typography from "../../styles/sbDesignSystem/sbTypography";
import { ClearThisFilter } from "./Common";

const toValidDate = (date: Date | null) => (date && isValid(date) ? date : null);
const resolveStyles = (theme: Theme) => ({
  // "& .MuiPickersCalendarHeader-label": {
  [`& .${pickersCalendarHeaderClasses.root}`]: {
    ...(typography && typography["body.medium.regular"]),
  },
  [`& .${dayCalendarClasses.weekDayLabel}`]: {
    ...(typography && typography["body.small.regular"]),
  },
  "& .MuiChip-filled.MuiChip-colorDefault": {
    fontSize: "10px",
  },
  [`& .${pickerDayClasses.selected}`]: {
    color: "text10.main",
    backgroundColor: theme.palette.primary50.main,
    borderRadius: "8px",
    "& :button": {
      backgroundColor: theme.palette.primary50.main,
    },
  },
  [`& .${dateRangePickerDayClasses.root}`]: {
    "&: last-of-type": {
      borderBottomRightRadius: "8px",
      borderTopRightRadius: "8px",
    },
    "&: first-of-type": {
      borderBottomLeftRadius: "8px",
      borderTopLeftRadius: "8px",
    },
  },
  [`& .${pickerDayClasses.today}`]: {
    borderColor: theme.palette.text50.main,
    borderRadius: "4px",
  },
});

const DateRangeFilters = ({
  start,
  end,
  onSelectDate,
  onClearThisFilter,
}: {
  start: Date | null;
  end: Date | null;
  onSelectDate: (date: { start: Date | null; end: Date | null }) => void;
  onClearThisFilter: () => void;
}) => {
  return (
    <>
      <Stack direction="row" sx={{ gap: "145px", mb: "5px" }}>
        <Typography sx={{ color: "text40.main" }} variant="body.xSmall.semibold">
          From
        </Typography>
        <Typography sx={{ color: "text40.main" }} variant="body.xSmall.semibold">
          To
        </Typography>
      </Stack>
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <MultiInputDateRangeField
          dateSeparator=""
          spacing="12px"
          data-testid="date-range-field"
          value={[start, end]}
          onChange={([start, end]: PickerRangeValue) => {
            onSelectDate({ start: toValidDate(start), end: toValidDate(end) });
          }}
          sx={{
            mb: "8px",
            [`& .${pickersInputBaseClasses.root}`]: {
              ...(typography && typography["body.xSmall.regular"]),
              height: "34px",
            },
          }}
          disableFuture
          onError={(reason, value) => {
            if (reason[1] === "disableFuture" && value[0]) {
              onSelectDate({ start: value[0], end: getCurrentUtcDate() });
            }
          }}
          maxDate={getCurrentUtcDate()}
        />
        <StaticDateRangePicker
          value={[start, end]}
          displayStaticWrapperAs="desktop"
          calendars={1}
          onChange={([start, end]: DateRange<Date>, context: { validationError: DateRangeValidationError }) => {
            if (context.validationError[1] === "disableFuture") {
              end = getCurrentUtcDate();
            }
            onSelectDate({ start: toValidDate(start), end: toValidDate(end) });
          }}
          slots={{
            actionBar: () => null,
          }}
          slotProps={{
            toolbar: {
              toolbarFormat: "MM/dd/yyyy",
            },
            layout: {
              sx: resolveStyles,
            },
            day: {
              sx: {
                "& .MuiButtonBase-root": {
                  fontSize: "12px",
                },
              },
            },
          }}
          disableFuture
        />
      </LocalizationProvider>
      {start && end ? <ClearThisFilter onClick={onClearThisFilter} /> : null}
    </>
  );
};

export { DateRangeFilters };
